"use client";

import { useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";
import InfoIcon from "@/assets/icons/info.svg";
import { Tooltip } from "../Tooltip";
import * as linkifyjs from "linkifyjs";
import linkifyHtml from "linkify-html";
import ReactContentEditable from "react-contenteditable";
import styles from "./ContentEditable.module.css";

export interface Props {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  placeholder?: string;
  label?: string;
  isError?: boolean;
  errorMsg?: string;
  alignCenter?: boolean;
  tooltipText?: string;
  className?: string;
  disabled?: boolean;
  tooltipIconClassName?: string;
  labelClassName?: string;
  errorMsgClassName?: string;
  onLinkDetect?: (links: string[]) => void;
  forceLightTheme?: boolean;
}

const linkify = (value: string) => {
  const node = linkifyHtml(value, {
    className: "text-red endemic:text-yellow",
    target: "_blank",
  });

  return node;
};

const ContentEditable = ({
  id,
  value,
  placeholder,
  maxLength,
  label,
  isError,
  errorMsg,
  tooltipText,
  className,
  alignCenter = true,
  disabled,
  tooltipIconClassName,
  labelClassName,
  errorMsgClassName,
  onChange,
  onLinkDetect,
  forceLightTheme,
}: Props) => {
  const contentEditable = useRef<HTMLDivElement>(null);

  const handleChange = (e: any) => {
    if (!disabled) {
      const result = e.currentTarget.innerText;
      onChange(result);
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.keyCode == 13 && e.key == "Enter") {
      e.preventDefault();
      e.stopPropagation();
      onChange(e.currentTarget.innerText + "\n");
    }
  };

  useEffect(() => {
    const result = linkifyjs.find(value);
    if (onLinkDetect) {
      onLinkDetect(
        result.filter((l: any) => l.type === "url").map((l: any) => l.href),
      );
    }
  }, [value, onLinkDetect]);

  return (
    <div
      className={`flex flex-col w-full ${alignCenter ? "items-center" : ""}`}
    >
      <div className="relative">
        {tooltipText && (
          <Tooltip
            text={tooltipText}
            className="lg:hidden absolute top-0 right-4"
            position={["left", "bottom"]}
            tooltipClassName="w-44"
          >
            <InfoIcon className="w-[18px] h-[18px] fill-secondary" />
          </Tooltip>
        )}
      </div>
      <label
        className={twMerge(
          "text-sm font-light tracking-tight text-primary px-4",
          labelClassName,
        )}
        htmlFor={id}
      >
        {label}
      </label>
      <div className="w-full flex items-center">
        <ReactContentEditable
          aria-placeholder={placeholder}
          innerRef={contentEditable}
          suppressContentEditableWarning={true}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          className={twMerge(
            "overflow-y-scroll custom-scroll w-full text-primary resize-none outline-none caret-green h-[100px] rounded-[20px] border-2 p-4 text-base font-light mt-[10px] bg-transparent endemic:text-white",
            `${
              isError
                ? "border-red"
                : "border-endemicGray-300 focus:border-green night:border-endemicGray-900 night:focus:border-green  endemic:focus:border-white"
            }`,
            className,
            forceLightTheme &&
              "endemic:text-invert endemic:focus:border-green endemic:placeholder:text-disabled",
            `${styles.customPlaceholder}`,
            forceLightTheme && `${styles.lightTheme}`,
          )}
          html={linkify(value)}
        />

        {tooltipText && (
          <Tooltip
            text={tooltipText}
            className="hidden lg:inline"
            tooltipClassName="w-44"
            position={["center", "top"]}
          >
            <div className="ml-10">
              <InfoIcon
                className={twMerge(
                  "w-[18px] h-[18px] fill-secondary endemic:fill-primary",
                  tooltipIconClassName,
                )}
              />
            </div>
          </Tooltip>
        )}
      </div>
      {maxLength && (
        <span
          className={twMerge(
            "text-sm font-light text-secondary endemic:text-grey px-4 pt-2",
            forceLightTheme && "endemic:text-disabled",
          )}
        >{`Characters left: ${value.length}/${maxLength}`}</span>
      )}
      <span
        className={twMerge(
          "text-red font-semibold text-xs px-4 h-[14px]",
          errorMsgClassName,
        )}
      >
        {isError ? errorMsg : ""}
      </span>
    </div>
  );
};

export default ContentEditable;
